import type {
  PreferredAppointmentAvailabilityType,
  DayAvailabilityType,
} from "../schema";

// These values map to the numbers defined in the backend
export enum RiskGuideline {
  BREAST = 0,
  PROSTATE = 1,
  CERVICAL = 2,
  COLON = 3,
  LUNG = 4,
  MRI = 5,
  COUNSELING = 6,
  TESTING = 7,
}

export enum ScreeningOption {
  GENETIC_TESTING = "geneticTesting",
  BREAST_MRI = "breastMRI",
  LUNG_CT = "lungCT",
}

interface NavigatorProps {
  currentTime: number;
  emailAddress: string;
  riskGuidelines: RiskGuideline[];
  appointmentAvailability: ReturnType<
    typeof getPreferredAppointmentAvailability
  >;
}

interface SpecialistProps {
  riskGuidelines: RiskGuideline[];
  appointmentAvailability: ReturnType<
    typeof getPreferredAppointmentAvailability
  >;
  currentTime: number;
  emailAddress: string;
}

export interface BuildPayloadsProps {
  email: string;
  currentTime: number;
  screenings: ScreeningOption[];
  preferredAppointmentAvailability: PreferredAppointmentAvailabilityType;
}

// "In Person Interested" status
const inPersonActionType = 17;
const personStatus = {
  status: 21, // IN_PERSON_INTERESTED
};

function getDayAvailability(dayAvailability: DayAvailabilityType) {
  const availability: Array<"AM" | "PM"> = [];

  if (dayAvailability.enabled && dayAvailability.am) {
    availability.push("AM");
  }
  if (dayAvailability.enabled && dayAvailability.pm) {
    availability.push("PM");
  }

  return availability.length > 0 ? availability : undefined;
}

function getPreferredAppointmentAvailability(
  appointmentAvailability: PreferredAppointmentAvailabilityType
) {
  return {
    start_time: Date.now(),
    stop_time: Date.now() + 360000,
    availability: {
      anytime: !!appointmentAvailability.anytime,
      monday: getDayAvailability(appointmentAvailability.monday),
      tuesday: getDayAvailability(appointmentAvailability.tuesday),
      wednesday: getDayAvailability(appointmentAvailability.wednesday),
      thursday: getDayAvailability(appointmentAvailability.thursday),
      friday: getDayAvailability(appointmentAvailability.friday),
    },
  };
}

function getNavigatorPayload({
  currentTime,
  emailAddress,
  riskGuidelines,
  appointmentAvailability,
}: NavigatorProps) {
  const personStatuses = riskGuidelines.length > 0 ? [personStatus] : [];
  const personContactHistory =
  {
    actionDateTime: currentTime,
    actionType: inPersonActionType,
    in_person_reason: 1, // IN_PERSON_INTERESTED
    user_email: emailAddress || "",
  };
  const personContactHistories = riskGuidelines.length > 0 ? [personContactHistory] : [];

  return {
    person: {
      preferred_appointment_times_attributes: [appointmentAvailability],
      person_statuses_attributes: personStatuses,
      person_contact_histories_attributes: personContactHistories,
      replace_appointments: true,
    },
  };
}

function getSpecialistPayload({
  riskGuidelines,
  currentTime,
  emailAddress,
  appointmentAvailability,
}: SpecialistProps) {
  const personStatuses = riskGuidelines.map((guideline) => ({
    ...personStatus,
    risk_guideline: guideline,
  }));

  const personContactHistories = riskGuidelines.map((guideline) => ({
    actionDateTime: currentTime,
    actionType: inPersonActionType,
    in_person_reason: 1, // IN_PERSON_INTERESTED
    user_email: emailAddress,
    risk_guideline: guideline,
  }));

  return {
    person: {
      replace_appointments: true,
      person_statuses_attributes: personStatuses,
      preferred_appointment_times_attributes: [appointmentAvailability],
      person_contact_histories_attributes: personContactHistories,
    },
  };
}

const SCREENING_CONFIG: Record<
  ScreeningOption,
  {
    specialistGuideline?: RiskGuideline;
    navigatorGuideline?: RiskGuideline;
  }
> = {
  [ScreeningOption.GENETIC_TESTING]: {
    navigatorGuideline: RiskGuideline.TESTING,
  },
  [ScreeningOption.BREAST_MRI]: {
    specialistGuideline: RiskGuideline.MRI,
  },
  [ScreeningOption.LUNG_CT]: {
    specialistGuideline: RiskGuideline.LUNG,
  },
};

export function buildPayloads({
  email,
  screenings,
  currentTime,
  preferredAppointmentAvailability,
}: BuildPayloadsProps) {

  const specialistRiskGuidelines: RiskGuideline[] = [];
  const navigatorRiskGuidelines: RiskGuideline[] = [];

  screenings.forEach((option) => {
    const config = SCREENING_CONFIG[option];

    if (config?.specialistGuideline !== undefined) {
      specialistRiskGuidelines.push(config.specialistGuideline);
    }

    if (config?.navigatorGuideline !== undefined) {
      navigatorRiskGuidelines.push(config.navigatorGuideline);
    }
  });

  const appointmentAvailability = getPreferredAppointmentAvailability(
    preferredAppointmentAvailability
  );
  const payloads: {
    navigatorPayload?: unknown;
    specialistPayload?: unknown;
  } = {};

  payloads.navigatorPayload = getNavigatorPayload({
    currentTime,
    emailAddress: email,
    riskGuidelines: navigatorRiskGuidelines,
    appointmentAvailability,
  });

  payloads.specialistPayload = getSpecialistPayload({
    currentTime,
    emailAddress: email,
    riskGuidelines: specialistRiskGuidelines,
    appointmentAvailability,
  });

  return payloads;
}
